import { useCallback, useEffect, useRef, useState } from 'react'
import { getFetchError, getIsFetching, invalidateFetch } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch, type Dispatch } from '@/utils/typeHelpers'
import { TAXFYLE_PORTAL_CONTACT_SUPPORT_ERROR } from './constants'

type TaxfylePortalLink = { jobUrl?: string; url?: string | null } | undefined

export interface TaxfylePortal {
  open: () => Promise<void>
  loading: boolean
  error: string | null
}

/** Generates a Taxfyle portal link on demand and opens it in the same tab. */
export const useOpenTaxfylePortal = (
  fetchLink: () => (dispatch: Dispatch) => Promise<TaxfylePortalLink>,
  fetchKey: string,
  { available = true }: { available?: boolean } = {}
): TaxfylePortal => {
  const dispatch = useAppDispatch()
  const isFetching = useReselector(getIsFetching, fetchKey)
  const fetchError = useReselector(getFetchError, fetchKey)
  const [opening, setOpening] = useState(false)
  const [noLink, setNoLink] = useState(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    const reset = () => setOpening(false)
    window.addEventListener('pageshow', reset)
    return () => window.removeEventListener('pageshow', reset)
  }, [])

  useEffect(
    () => () => {
      dispatch(invalidateFetch(fetchKey))
    },
    [dispatch, fetchKey]
  )

  const open = useCallback(async () => {
    if (isFetching) {
      return
    }
    if (!available) {
      setNoLink(true)
      return
    }
    setNoLink(false)
    const link = await fetchLink()(dispatch)
    // A link that arrives after the user navigated away must not pull them back out.
    if (!mounted.current) {
      return
    }
    const url = link?.jobUrl ?? link?.url
    if (url) {
      setOpening(true)
      window.open(url, '_self', 'noreferrer')
      return
    }
    setNoLink(true)
  }, [available, dispatch, fetchLink, isFetching])

  const noLinkError = noLink ? TAXFYLE_PORTAL_CONTACT_SUPPORT_ERROR : null

  return {
    open,
    loading: isFetching || opening,
    error: fetchError?.message ?? noLinkError,
  }
}
